import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { queryKeys } from "./query-keys";

// Types
export interface Project {
  id: number;
  title: string;
  slug: string;
  description: string | null;
  category: string | null;
  url: string | null;
  image_url: string | null;
  display_order: number;
  visible: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateProjectInput {
  title: string;
  slug: string;
  description?: string;
  category?: string;
  url?: string;
  image_url?: string;
  display_order?: number;
  visible?: boolean;
}

export interface UpdateProjectInput extends CreateProjectInput {
  id: number;
}

// Fetch functions
async function fetchProjects(): Promise<Project[]> {
  const response = await fetch("/api/projects");
  if (!response.ok) throw new Error("Failed to fetch projects");
  const data = (await response.json()) as { projects: Project[] };
  return data.projects;
}

async function fetchProject(slug: string): Promise<Project> {
  const response = await fetch(`/api/projects/${slug}`);
  if (!response.ok) throw new Error("Failed to fetch project");
  const data = (await response.json()) as { project: Project };
  return data.project;
}

async function fetchAdminProjects(): Promise<Project[]> {
  const response = await fetch("/api/admin/projects");
  if (!response.ok) throw new Error("Failed to fetch projects");
  const data = (await response.json()) as { projects: Project[] };
  return data.projects;
}

async function createProject(input: CreateProjectInput): Promise<Project> {
  const response = await fetch("/api/admin/projects", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(input),
  });
  if (!response.ok) throw new Error("Failed to create project");
  const data = (await response.json()) as { project: Project };
  return data.project;
}

async function updateProject(input: UpdateProjectInput): Promise<Project> {
  const { id, ...body } = input;
  const response = await fetch(`/api/admin/projects/${id}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body),
  });
  if (!response.ok) throw new Error("Failed to update project");
  const data = (await response.json()) as { project: Project };
  return data.project;
}

async function deleteProject(id: number): Promise<void> {
  const response = await fetch(`/api/admin/projects/${id}`, {
    method: "DELETE",
  });
  if (!response.ok) throw new Error("Failed to delete project");
}

// Hooks
export function useProjects() {
  return useQuery({
    queryKey: queryKeys.projects.list(),
    queryFn: fetchProjects,
  });
}

export function useProject(slug: string) {
  return useQuery({
    queryKey: queryKeys.projects.detail(slug),
    queryFn: () => fetchProject(slug),
    enabled: !!slug,
  });
}

export function useAdminProjects() {
  return useQuery({
    queryKey: queryKeys.projects.admin.list(),
    queryFn: fetchAdminProjects,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: createProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.admin.all });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: updateProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.admin.all });
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: deleteProject,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
      queryClient.invalidateQueries({ queryKey: queryKeys.projects.admin.all });
    },
  });
}
